import { forwardRef } from "react";
import classes from "./Input.module.scss";
import { clsx } from "clsx";
import { InputProps } from "./InputProps";
import { useFormControl, useErrorDescribedBy } from "@/hooks/useFormControl";
import { CheckSharp } from "@/components/ui/Icon/CheckSharp";
import { InputAdornment } from "@/components/ui/InputAdornment";

export const Input = forwardRef<HTMLInputElement, InputProps>(
  function Input(props, ref) {
    const {
      className,
      id,
      startAdornment,
      hasError,
      size = "medium",
      type = "text",
      onChange,
      "aria-describedby": describedByProp,
      ...htmlProps
    } = props;

    const formControl = useFormControl();
    const describedBy = useErrorDescribedBy(describedByProp);
    const inputId = id ?? formControl?.id;
    const inputHasError = hasError ?? formControl?.hasError ?? false;
    const isValid = formControl?.isValid && !inputHasError;

    const inputClasses = clsx(classes.input, classes[size], {
      [classes.endAdornmentSpace]: isValid,
      [classes.startAdornmentSpace]: startAdornment,
    });

    return (
      <div className={clsx(classes.wrapper, className)}>
        {startAdornment}

        <input
          type={type}
          ref={ref}
          id={inputId}
          className={inputClasses}
          onChange={(e) => {
            if (type === "email" && e.target.value) {
              e.target.value = e.target.value.toLowerCase();
            }
            onChange?.(e);
          }}
          {...(inputHasError && { "aria-invalid": true })}
          {...htmlProps}
          aria-describedby={describedBy}
        />

        {isValid && (
          <InputAdornment position="end" isInteractive={false}>
            <CheckSharp
              size={24}
              strokeWidth={4}
              stroke="currentColor"
              className={classes.check}
              data-testid="input-valid-icon"
            />
          </InputAdornment>
        )}
      </div>
    );
  },
);
